import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import * as commentsApi from '../../api/comments';
import type { Comment, CommentPage, CommentTargetType } from '../../types/comments';
import { commentKeys } from './commentKeys';

const PAGE_SIZE = 20;

export interface UseCommentsResult {
  comments: Comment[];
  isLoading: boolean;
  isError: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** Server returned 403 — caller should render the no-access banner. */
  isForbidden: boolean;
  refetch: () => void;
}

function isForbiddenError(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 403;
}

/**
 * Cursor-paginated top-level comments for a target. Pages descend by id, so
 * the next page's `before` cursor is the oldest id we've seen so far.
 */
export function useComments(
  targetType: CommentTargetType,
  targetId: string | null | undefined,
  enabled = true
): UseCommentsResult {
  const query = useInfiniteQuery<CommentPage>({
    queryKey: targetId
      ? commentKeys.list(targetType, targetId)
      : ['comments', 'list', targetType, 'none'],
    queryFn: ({ pageParam }) =>
      commentsApi.listComments(targetType, targetId!, {
        before: typeof pageParam === 'string' ? pageParam : undefined,
        limit: PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!targetId && enabled,
    retry: (count, err) => (isForbiddenError(err) ? false : count < 2),
  });

  const flat = ((query.data as InfiniteData<CommentPage> | undefined)?.pages ?? []).flatMap(
    (p) => p.comments
  );

  return {
    comments: flat,
    isLoading: query.isLoading,
    isError: query.isError && !isForbiddenError(query.error),
    fetchNextPage: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage();
    },
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isForbidden: isForbiddenError(query.error),
    refetch: () => query.refetch(),
  };
}
